import React, { useState, useEffect } from "react";
import styles from "./ImageViewer.module.css";
import { ImageViewerProps } from "./ImageViewer.types";
import { fetchImageAsBlob } from "./ImageViewer.service";
import { ThreeCircles } from "react-loader-spinner";

/**
 * ImageViewer Component
 * Displays supported image formats with consistent styling to SpreadsheetViewer.
 */
const ImageViewer: React.FC<ImageViewerProps> = ({ preview }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let loaderTimeout: NodeJS.Timeout;
    if (loading) {
      loaderTimeout = setTimeout(() => setShowLoader(true), 300);
    } else {
      setShowLoader(false);
    }
    return () => clearTimeout(loaderTimeout);
  }, [loading]);

  useEffect(() => {
    const loadImage = async () => {
      if (!preview?.url) return;
      try {
        setLoading(true);
        setError(null);
        const blob = await fetchImageAsBlob(preview.url);
        const objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      } catch (err) {
        console.error("Error loading image:", err);
        setError("Failed to load image. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadImage();
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [preview?.url]);

  return (
    <div className={styles.imageViewerWrapper}>
      {preview?.url ? (
        <>
          <div className={styles.toolbar}>
            <div className={styles.fileTitle}>{preview?.name}</div>
          </div>

          {showLoader && (
            <div className={styles.customGridLoader}>
              <ThreeCircles
                height="50"
                width="50"
                color="#4fa94d"
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
              <div className={styles.customGridLoaderText}>
                Loading your image
              </div>
            </div>
          )}

          {!loading && !error && imageSrc && (
            <div className={styles.imageContainer}>
              <img
                src={imageSrc}
                alt={preview?.name || "Preview image"}
                className={styles.imageDisplay}
              />
            </div>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}
        </>
      ) : (
        <span className={styles.previewFileMessage}>
          Please select an image to preview.
        </span>
      )}
    </div>
  );
};

export default ImageViewer;
